import React, { useEffect, useMemo, useState } from "react";
import { Avatar, Col, Layout, Menu, Row, Space, Typography, theme, Tooltip, Spin } from "antd";
import { useLocalStorage } from "@rehooks/local-storage";
import type { MenuProps } from "antd";
import {
  DatabaseOutlined,
  UserOutlined,
  AppstoreOutlined,
  ApartmentOutlined,
  DashboardOutlined,
  DeploymentUnitOutlined,
  FunctionOutlined,
  AppleFilled,
  AppleOutlined,
  ToolOutlined,
  FileTextOutlined,
  StarOutlined,
} from "@ant-design/icons";
import { Link, Outlet, useLocation } from "react-router-dom";
import { AppRoutes } from "../AppRoutes";
import { SessionContext } from "../providers/SessionProvider";
import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Footer } from "antd/es/layout/layout";
import { WizardService } from "../services/WizardService";
import { VersionInfo } from "../types/validator";

const { Header, Content, Sider } = Layout;
const { Text } = Typography;
const { useToken } = theme;

export interface AuthorizedRootProps {
  children?: {};
}

type MenuItem = Required<MenuProps>['items'][number];

// Maps the current path to its parent submenu group key so the sidebar can seed
// `openKeys` with the group containing the page the user landed on.
const getDefaultOpenKeys = (pathname: string): string[] => {
  const path = pathname.replace(/^\//, ""); // normalize leading slash, see Risks in the spec
  if (path.startsWith("wizard/")) return ["wizard_group"];
  if (path.startsWith("builder/")) return ["builder_group"];
  return [];
};

// Wizard menu item keys come from `AppRoutes.Wizard.*`, which are defined without a
// leading slash (e.g. "wizard/mapping"), while `location.pathname` always has one.
// Strip it for wizard routes so Menu's `selectedKeys` lines up with the menu item keys
// and the sidebar highlights the active Wizard entry. Other top-level routes
// (Creatives, VastBuilder, ...) keep their leading slash since their keys have one too.
const getSelectedMenuKey = (pathname: string): string =>
  pathname.startsWith("/wizard/") ? pathname.slice(1) : pathname;

export const AuthorizedPage = () => {
  const { token } = useToken();

  const [isCollapsed, setCollapsed] = useLocalStorage("sider_collapsed", false);
  const location = useLocation();
  const [openKeys, setOpenKeys] = useLocalStorage<string[]>(
    "sider_open_keys",
    getDefaultOpenKeys(location.pathname)
  );
  const session = useContext(SessionContext);

  const isAuthorized = session.user != null;
  const navigate = useNavigate();

  useEffect(() => {
    if (session.loading) return;
    if (!isAuthorized) {
      navigate(AppRoutes.Login);
    }
  }, [isAuthorized, navigate, session.loading]);

  const menuItems: MenuItem[] = useMemo(() => {
    const wizardChildren: MenuItem[] = [];

    wizardChildren.push(
      {
        key: AppRoutes.Wizard.MappingList,
        icon: <ApartmentOutlined />,
        label: <Link to={AppRoutes.Wizard.MappingList}>Mapping</Link>,
      },
      {
        key: AppRoutes.Wizard.SdkList,
        icon: <AppstoreOutlined />,
        label: <Link to={AppRoutes.Wizard.SdkList}>Releases</Link>,
      },
      {
        key: AppRoutes.Wizard.Recommendations,
        icon: <StarOutlined />,
        label: <Link to={AppRoutes.Wizard.Recommendations}>Recommended SDKs</Link>,
      },
      {
        key: AppRoutes.Wizard.Spm,
        icon: <DeploymentUnitOutlined />,
        label: <Link to={AppRoutes.Wizard.Spm}>SPM</Link>,
      },
      {
        key: AppRoutes.Wizard.Crawler,
        icon: <FunctionOutlined />,
        label: <Link to={AppRoutes.Wizard.Crawler}>Crawler</Link>,
      },
      {
        key: AppRoutes.Wizard.SystemHealth,
        icon: <DashboardOutlined />,
        label: <Link to={AppRoutes.Wizard.SystemHealth}>System Health</Link>,
      },
      {
        key: AppRoutes.Wizard.SKAdNetwork,
        icon: <AppleFilled />,
        label: <Link to={AppRoutes.Wizard.SKAdNetwork}>SKAdNetwork</Link>,
      },
      {
        key: AppRoutes.Wizard.AdAttributionKit,
        icon: <AppleOutlined />,
        label: <Link to={AppRoutes.Wizard.AdAttributionKit}>AdAttributionKit</Link>,
      }
    );

    return [
      {
        key: "wizard_group",
        icon: <AppstoreOutlined />,
        label: "Wizard",
        children: wizardChildren,
      },
      {
        key: AppRoutes.Creatives,
        icon: <DatabaseOutlined />,
        label: <Link to={AppRoutes.Creatives}>Creatives</Link>,
      },
      {
        key: "builder_group",
        icon: <ToolOutlined />,
        label: "Creative Builder",
        children: [
          {
            key: AppRoutes.VastBuilder,
            label: <Link to={AppRoutes.VastBuilder}>VAST</Link>,
          },
          {
            key: AppRoutes.AdaptiveRendering,
            label: <Link to={AppRoutes.AdaptiveRendering}>Adaptive Rendering</Link>,
          },
          {
            key: AppRoutes.CreativeValidator,
            label: <Link to={AppRoutes.CreativeValidator}>Creative Validator</Link>,
          },
        ],
      },
    ];
  }, []);

  const AppHeader = () => {
    return (
      <Header>
        <Row>
          <Col flex="auto"></Col>
          <Col flex="right">
            <Space size={16}>
              <Link to="/user" className="greeting">
                <Space>
                  <Text strong>{session.user?.name}</Text>
                  <Avatar
                    shape="circle"
                    size={token.Avatar?.containerSizeSM}
                    icon={<UserOutlined />}
                  />
                </Space>
              </Link>
            </Space>
          </Col>
        </Row>
      </Header>
    );
  };

  const AppSider = React.memo(() => {
    return (
      <Sider
        theme={"light"}
        collapsible
        width={260}
        collapsed={isCollapsed!!}
        onCollapse={setCollapsed}
      >
        <div
          style={{
            lineHeight: token.Layout?.headerHeight,
            margin: "1rem 0",
            width: "100%",
          }}
        >
          <div
            style={{
              display: "flex",
              verticalAlign: "center",
              justifyContent: "center",
            }}
          >
            <Avatar
              style={{ backgroundColor: "#fff", boxShadow: token.boxShadow }}
              shape="square"
              size={token.Avatar?.containerSizeLG}
            >
              <Text
                style={{
                  fontSize: token.fontSizeSM,
                  fontWeight: "bolder",
                  color: "#e84039",
                }}
              >
                MDS
              </Text>
            </Avatar>
          </div>
        </div>

        <Menu
          mode="inline"
          style={{ height: "100vh" }}
          selectedKeys={[getSelectedMenuKey(location.pathname)]}
          openKeys={openKeys}
          onOpenChange={(keys) => setOpenKeys(keys)}
          items={menuItems}
        />
      </Sider>
    );
  });

  const AppFooter = () => {
    const [versionInfo, setVersionInfo] = useState<VersionInfo | null>(null);

    useEffect(() => {
      WizardService.Version.get()
        .then((info) => setVersionInfo(info))
        .catch(() => {});
    }, []);

    const formatBuildTime = (buildTime: string) => {
      const date = new Date(buildTime);
      return date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    };

    return (
      <Footer
        style={{
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Space size="large" style={{ marginBottom: 8 }}>
          {versionInfo && (
            <Tooltip
              title={`Revision: ${versionInfo.revision} | Built: ${formatBuildTime(versionInfo.buildTime)}`}
            >
              <Text
                style={{
                  fontSize: 16,
                  cursor: "help",
                  background: token.colorBgContainer,
                  padding: "4px 12px",
                  borderRadius: token.borderRadius,
                }}
              >
                {versionInfo.version}
              </Text>
            </Tooltip>
          )}
          <a
            href={`${process.env.REACT_APP_API_NEW_URL}/logs`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontSize: 16,
              color: token.colorText,
              background: token.colorBgContainer,
              padding: "4px 12px",
              borderRadius: token.borderRadius,
            }}
          >
            <FileTextOutlined /> Logs
          </a>
        </Space>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Appodeal Mediation Development Services ©{new Date().getFullYear()}
        </Text>
      </Footer>
    );
  };

  if (session.loading) {
    return (
      <Layout style={{ minHeight: "100vh", justifyContent: "center", alignItems: "center" }}>
        <Spin size="large" />
      </Layout>
    );
  }

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <AppSider />
      <Layout>
        <AppHeader />
        <Content style={{ padding: "1rem" }}>
          <Outlet />
        </Content>
        <AppFooter />
      </Layout>
    </Layout>
  );
};
